import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { CreateBriefService } from '../briefs/service/create-brief/create-brief.service';
import { BriefService } from '../../services/brief/brief.service';
import { AlertService } from '../../atoms/alert/alert.service';
import { BriefResponseDto } from '../../common/general-types/briefs.dto';
import { LoadingOverlayProgressComponent } from '../../atoms/loading-overlay-progress/loading-overlay-progress.component';
import { FlowChooser, BriefFlow } from './flows/flow-chooser/flow-chooser';
import { WizardFlow } from './flows/wizard-flow/wizard-flow';
import { AiFirstFlow } from './flows/ai-first-flow/ai-first-flow';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { GaService } from '../../services/ga/ga';
import {
  CREATE_BRIEF_REGULAR_MILESTONES,
  type CreateBriefRegularEntry,
  type CreateBriefRegularFlow,
} from '../../services/ga/create-brief-regular.analytics';
import {
  SCREENING_PII_ALERT,
  withoutProtectedScreeningQuestions,
} from './sections/screening-section/screening-pii.helpers';

type Screen = 'chooser' | 'ai' | 'wizard';

/**
 * Dual-flow brief builder. Reuses CreateBriefService + sendFinalBrief() (same outcome as
 * CreateBriefBasicComponent) but with the new Claude Design UX and no pricingUserType branching.
 */
@Component({
  selector: 'app-create-brief-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FlowChooser, WizardFlow, AiFirstFlow, LoadingOverlayProgressComponent],
  templateUrl: './create-brief-select.html',
  styleUrl: './create-brief-select.scss',
})
export class CreateBriefSelect implements OnInit {
  
  /** Services */
  readonly createBriefService = inject(CreateBriefService);
  private readonly briefService = inject(BriefService);
  private readonly alertService = inject(AlertService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly gaService = inject(GaService);
  /** Services - END */

  readonly screen = signal<Screen>('chooser');

  private launchApiDone = false;
  private launchAnimationDone = false;
  private pendingBriefResult: BriefResponseDto | null = null;

  ngOnInit(): void {
    let entrySource: CreateBriefRegularEntry = 'default';

    if (this.briefService.launchingFromRetroSuggestedBrief) {
      this.briefService.launchingFromRetroSuggestedBrief = false;
      this.screen.set('ai');
      entrySource = 'retro';
    } else if (this.briefService.duplicatingBrief) {
      // State was hydrated by populateFromBriefForDuplicate(); keep it and jump into editing.
      this.briefService.duplicatingBrief = false;
      this.screen.set('wizard');
      entrySource = 'duplicate';
    } else if (this.briefService.resumingDraft) {
      this.briefService.resumingDraft = false;
      this.screen.set('wizard');
    } else {
      this.createBriefService.resetBriefBuilder();
    }

    this.createBriefService.requiresPieceList.set(true);

    // This flow keeps only the read-only "regular" compensation path.
    this.createBriefService.pricingUserType.set('regular');
    this.createBriefService.getPricingModelOptions();

    this.trackLanding(entrySource);
  }

  chooseFlow(flow: BriefFlow): void {
    this.screen.set(flow);
    this.gaService.trackRegularBriefMilestone(CREATE_BRIEF_REGULAR_MILESTONES.flowSelected, {
      flow,
    });
  }

  switchFlow(): void {
    const previousFlow = this.screen();
    this.gaService.trackRegularBriefMilestone(CREATE_BRIEF_REGULAR_MILESTONES.switchFlow, {
      flow: previousFlow === 'chooser' ? undefined : (previousFlow as CreateBriefRegularFlow),
    });
    this.screen.set('chooser');
  }

  startOver(): void {
    this.gaService.trackRegularBriefMilestone(CREATE_BRIEF_REGULAR_MILESTONES.startOver);
    this.createBriefService.resetBriefBuilder();
    this.createBriefService.requiresPieceList.set(true);
    this.createBriefService.pricingUserType.set('regular');
    this.createBriefService.getPricingModelOptions();
    this.screen.set('chooser');
  }

  exit(): void {
    this.gaService.trackRegularBriefMilestone(CREATE_BRIEF_REGULAR_MILESTONES.exit, {
      flow: this.currentFlow(),
    });
    this.location.back();
  }

  /** Submit the brief. Mirrors CreateBriefBasicComponent.submitBrief() minus the pricingUserType branch. */
  submit(): void {
    const screening = this.createBriefService.screeningQuestions();
    const allowedScreening = withoutProtectedScreeningQuestions(screening);
    if (allowedScreening.length !== screening.length) {
      this.createBriefService.screeningQuestions.set(allowedScreening);
      void this.alertService.alert(SCREENING_PII_ALERT);
      return;
    }

    this.gaService.trackRegularBriefMilestone(CREATE_BRIEF_REGULAR_MILESTONES.launchClicked, {
      flow: this.currentFlow(),
    });

    this.launchApiDone = false;
    this.launchAnimationDone = false;
    this.pendingBriefResult = null;

    this.createBriefService.sendFinalBrief().subscribe({
      next: (res: BriefResponseDto) => {
        this.gaService.trackRegularBriefMilestone(CREATE_BRIEF_REGULAR_MILESTONES.launchSuccess, {
          flow: this.currentFlow(),
          brief_id: res.id,
        });
        this.pendingBriefResult = res;
        this.launchApiDone = true;
        this.tryFinishLaunch();
      },
      error: (error) => {
        console.error('Error creating brief: ', error);
        this.gaService.trackRegularBriefMilestone(CREATE_BRIEF_REGULAR_MILESTONES.launchError, {
          flow: this.currentFlow(),
        });
        this.createBriefService.clearProgressSimulation();
        this.createBriefService.progress_indicator.set(0);
        this.alertService.alert({
          title: 'Error creating brief',
          message: 'Please try again.',
          confirmText: 'OK',
          cancelText: 'Cancel',
          type: 'alert',
          icon: 'info',
        });
      },
    });
  }

  onLaunchAnimationComplete(): void {
    this.launchAnimationDone = true;
    this.tryFinishLaunch();
  }

  private tryFinishLaunch(): void {
    if (!this.launchApiDone || !this.launchAnimationDone || !this.pendingBriefResult) {
      return;
    }

    const res = this.pendingBriefResult;
    this.pendingBriefResult = null;
    this.createBriefService.resetBriefBuilder();
    this.alertService
      .confirm({
        title: 'Brief created successfully!',
        message: 'You can now view your brief in the briefs page.',
        confirmText: 'Manage Brief',
        cancelText: 'Back to Briefs',
        type: 'confirm',
        icon: 'success',
      })
      .then((result) => {
        if (result) {
          this.router.navigate(['/manage-brief', res.id, 2]);
        } else {
          this.location.back();
        }
      });
  }

  private currentFlow(): CreateBriefRegularFlow | undefined {
    const screen = this.screen();
    return screen === 'wizard' || screen === 'ai' ? screen : undefined;
  }

  private trackLanding(entrySource: CreateBriefRegularEntry): void {
    const flow = this.currentFlow();
    this.gaService.trackRegularBriefMilestone(CREATE_BRIEF_REGULAR_MILESTONES.landing, {
      entry_source: entrySource,
      ...(flow ? { flow } : {}),
    });
  }
}
